import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import { api, API_BASE } from "../../../services/api";
import toast from "react-hot-toast";

import "./LoginPage.css";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#fbbc05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#ea4335"
    />
  </svg>
);

const LoginPage = ({ onLogin, initialMode = "login" }) => {
  const { identity } = useTheme();
  const isTeacherView = identity === "teacher";
  
  const [formMode, setFormMode] = useState(initialMode);
  const [forgotStep, setForgotStep] = useState("email");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const showDemo = process.env.REACT_APP_SHOW_DEMO === "true";

  const [credentials, setCredentials] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    otp: "",
    newPassword: "",
    confirmNewPassword: "",
    institutionName: "",
  });

  const [platformInfo, setPlatformInfo] = useState({
    platformName: "EduSync",
    maintenanceMode: false,
  });

  const navigate = useNavigate();

  const handleTokenLogin = useCallback(
    async (token) => {
      setLoading(true);
      setError("");
      try {
        localStorage.setItem("adminUser", JSON.stringify({ token }));
        const res = await api.getMe();
        if (res.data.success) {
          window.history.replaceState({}, document.title, window.location.pathname);
          onLogin({ ...res.data.admin, token });
          toast.success(`Logged in as ${res.data.admin.name}`);
          navigate("/dashboard");
        }
      } catch (err) {
        localStorage.removeItem("adminUser");
        setError("Impersonation session failed or expired");
      } finally {
        setLoading(false);
      }
    },
    [onLogin, navigate],
  );

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await api.getPublicInfo();
        if (res.data?.success) setPlatformInfo(res.data);
      } catch (err) {
        console.error("Failed to fetch info", err);
      }
    };
    fetchInfo();

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) handleTokenLogin(token);
  }, [handleTokenLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (formMode === "register") {
        if (credentials.password !== credentials.confirmPassword) {
          setError("Passwords do not match!");
          setLoading(false);
          return;
        }
        const res = await api.registerAdmin({
          name: credentials.username,
          email: credentials.email,
          password: credentials.password,
          institutionName: credentials.institutionName,
        });
        if (res.data.success) {
          toast.success("Registration successful! Verify your email.");
          setFormMode("login");
        }
      } else if (formMode === "forgot") {
        if (forgotStep === "email") {
          const res = await api.forgotPasswordAdmin({ email: credentials.email });
          if (res.data.success) {
            toast.success("OTP sent!");
            setForgotStep("otp");
          }
        } else if (forgotStep === "otp") {
          setForgotStep("newpass");
        } else if (forgotStep === "newpass") {
          if (credentials.newPassword !== credentials.confirmNewPassword) {
            setError("Passwords do not match!");
            setLoading(false);
            return;
          }
          const res = await api.resetPasswordAdmin({
            email: credentials.email,
            otp: credentials.otp,
            newPassword: credentials.newPassword,
          });
          if (res.data.success) {
            toast.success("Password reset successful!");
            setFormMode("login");
          }
        }
      } else {
        const res = await api.loginAdmin({
          email: credentials.email,
          password: credentials.password,
          intendedRole: identity
        });
        if (res.data.success) {
          onLogin({ ...res.data.admin, token: res.data.token });
          navigate("/dashboard");
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const switchMode = (mode) => {
    setFormMode(mode);
    setError("");
    setForgotStep("email");
  };

  const getTitle = () => {
    if (formMode === "register") return "Institutional Portal";
    if (formMode === "forgot") return "Reset Access";
    return isTeacherView ? "Teacher Portal" : "Admin Portal";
  };

  return (
    <div className="login-split-wrapper" data-identity={identity}>

      {/* ── LEFT PANEL ── */}
      <div className="split-left">
        {/* 4 decorative pulsing rings */}
        <div className="pulse-ring" />
        <div className="pulse-ring" />
        <div className="pulse-ring" />
        <div className="pulse-ring" />

        {/* Slow-rotating SVG polygon */}
        <svg className="geo-rotate" width="420" height="420" viewBox="0 0 420 420">
          <polygon points="210,12 408,408 12,408" fill="none" stroke="white" strokeWidth=".8"/>
          <polygon points="210,55 370,380 50,380" fill="none" stroke="white" strokeWidth=".4"/>
          <circle cx="210" cy="210" r="195" fill="none" stroke="white" strokeWidth=".4"/>
          <circle cx="210" cy="210" r="130" fill="none" stroke="white" strokeWidth=".3"/>
        </svg>

        {/* Ghosted vertical brand text */}
        <div className="brand-ghost">Edusync</div>

        {/* Top: logo + platform name */}
        <div className="split-left-top">
          <div className="left-eyebrow">{platformInfo.platformName} Platform</div>
          <div className="left-logo-container">
            <img src="/Landscape_Logo.png" alt="EduSync Logo" className="login-logo-img" />
          </div>
        </div>

        {/* Bottom: role badge + tagline */}
        <div className="split-left-bottom">
          <div className="role-badge">
            {isTeacherView ? 'Educator' : 'administrator'}
          </div>
          <div className="left-tagline">
            {isTeacherView ? 'Where knowledge\nbecomes legacy.' : 'Command.\nControl.\nEmpower.'}
          </div>
          <div className="left-divider" />
          <div className="left-hint">Secure Access Portal</div>
        </div>

        {/* Right-edge separator line */}
        <div className="panel-sep" />
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="split-right">
        <button className="back-btn-noir" onClick={() => navigate("/")} title="Back to Home">
          <FaArrowLeft />
        </button>

        <div className="form-container fade-in">
          <div className="form-eyebrow">
            {isTeacherView ? 'Teacher Portal' : 'Admin Portal'}
          </div>
          <h1 className="form-title">{getTitle()}</h1>

          {error && <div className="error-noir"><span>⚠</span> {error}</div>}

          <form className="portal-form" onSubmit={handleSubmit}>
            {formMode === "register" && (
              <>
                <div className="portal-group">
                  <label className="portal-label">Full Name</label>
                  <div className="inp-wrap">
                    <input
                      type="text"
                      name="username"
                      className="portal-input"
                      placeholder="John Doe"
                      value={credentials.username}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="portal-group">
                  <label className="portal-label">Institution Name</label>
                  <div className="inp-wrap">
                    <input
                      type="text"
                      name="institutionName"
                      className="portal-input"
                      placeholder="Global Academy"
                      value={credentials.institutionName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div className="portal-group">
              <label className="portal-label">Institutional Email</label>
              <div className="inp-wrap">
                <input
                  type="email"
                  name="email"
                  className="portal-input"
                  placeholder="email@institution.edu"
                  value={credentials.email}
                  onChange={handleChange}
                  required
                  disabled={formMode === "forgot" && forgotStep !== "email"}
                />
              </div>
            </div>

            {formMode === "forgot" && forgotStep === "otp" && (
              <div className="portal-group swipe-in">
                <label className="portal-label">Security OTP</label>
                <div className="inp-wrap">
                  <input
                    type="text"
                    name="otp"
                    className="portal-input"
                    placeholder="000000"
                    value={credentials.otp}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            {formMode === "forgot" && forgotStep === "newpass" && (
              <>
                <div className="portal-group swipe-in">
                  <label className="portal-label">New Password</label>
                  <div className="inp-wrap">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      className="portal-input"
                      value={credentials.newPassword}
                      onChange={handleChange}
                      required
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowNewPassword(!showNewPassword)}>
                      {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div className="portal-group swipe-in">
                  <label className="portal-label">Confirm Password</label>
                  <div className="inp-wrap">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmNewPassword"
                      className="portal-input"
                      value={credentials.confirmNewPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {formMode !== "forgot" && (
              <div className="portal-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="portal-label">Password</label>
                  {formMode === "login" && (
                    <button type="button" className="forgot-link" onClick={() => switchMode("forgot")}>
                      Lost Access?
                    </button>
                  )}
                </div>
                <div className="inp-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="portal-input"
                    value={credentials.password}
                    onChange={handleChange}
                    required
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            )}

            {formMode === "register" && (
              <div className="portal-group">
                <label className="portal-label">Verify Password</label>
                <div className="inp-wrap">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    className="portal-input"
                    value={credentials.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            <button type="submit" className="portal-btn" disabled={loading}>
              {loading ? "Authenticating..." : formMode === "login" ? "Enter Portal" : "Initialize Account"}
            </button>
          </form>

          {(formMode === "login" || formMode === "register") && (
            <>
              <div className="portal-footer" style={{ margin: '24px 0' }}>— OR —</div>
              <button
                type="button"
                className="google-btn-noir"
                onClick={() => {
                  const mode = formMode === "register" ? "register" : "login";
                  window.location.href = `${API_BASE}/admin/google?mode=${mode}&intendedRole=${identity}`;
                }}
              >
                <GoogleIcon />
                Continue with Institutional Google
              </button>
            </>
          )}

          <div className="portal-footer">
            {formMode === "register" ? (
              <>
                Already registered? 
                <button className="footer-link" onClick={() => switchMode("login")}>Access Portal</button>
              </>
            ) : formMode === "forgot" ? (
              <button className="footer-link" onClick={() => switchMode("login")}>Back to Security Portal</button>
            ) : !isTeacherView ? (
              <>
                Institutional Admin?
                <button className="footer-link" onClick={() => switchMode("register")}>Register School</button>
              </>
            ) : (
              <div style={{ color: 'var(--violet-accent)', opacity: 0.8 }}>
                Authorized Teacher Personnel Only
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default LoginPage;




